import { useMutation } from "react-query";
import type { UseMutationResult } from "react-query";
import { t } from "@/i18n-js/instance";
import { paywallChargesApi } from "@/react/api/paywallChargesApi";
import type { RefundChargeResponse } from "@/react/api/paywallChargesApi";
import type { CommunityMemberCharge } from "@/react/types/CommunityMemberCharge";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { useToast } from "@circle-react-uikit/ToastV2";

const { refundCharge } = paywallChargesApi;

interface RefundChargeCallback {
  refundCharge: {
    onSuccess: () => void;
    onError: () => void;
  };
}

interface UsePaywallRefundChargeProps {
  callback: RefundChargeCallback;
  charge: CommunityMemberCharge;
}

interface RefundChargeParams extends Record<string, unknown> {
  amount: number;
  reason_details?: string;
}

interface UsePaywallRefundChargeReturn {
  refundMutation: UseMutationResult<
    RefundChargeResponse,
    ApiError,
    RefundChargeParams
  >;
}

export const usePaywallRefundCharge = ({
  charge,
  callback,
}: UsePaywallRefundChargeProps): UsePaywallRefundChargeReturn => {
  const maximumAmountToRefund = charge.amount_to_refund_raw;
  const { success, error } = useToast();
  const defaultErrorMessage = t(
    "settings.paywalls_admin_dashboard.charges.refund_modal.alerts.error.default_message",
  );

  const refundMutation = useMutation<
    RefundChargeResponse,
    ApiError,
    RefundChargeParams
  >(
    (params: RefundChargeParams) =>
      refundCharge({
        chargeProcessorId: charge.processor_id,
        params,
      }),
    {
      onSuccess: (_, requestPayload: RefundChargeParams) => {
        const successMessage =
          requestPayload.amount === maximumAmountToRefund
            ? t(
                "settings.paywalls_admin_dashboard.charges.refund_modal.alerts.success.refund_succeed",
              )
            : t(
                "settings.paywalls_admin_dashboard.charges.refund_modal.alerts.success.partial_refund_succeed",
              );

        callback.refundCharge.onSuccess();
        success(successMessage);
      },
      onError: (apiError: ApiError) => {
        callback.refundCharge.onError();
        const errorMessage = apiError.message ?? defaultErrorMessage;
        console.error(errorMessage);
        error(errorMessage, { duration: "long" });
      },
    },
  );

  return {
    refundMutation,
  };
};
